import { useCallback, useState } from "react";

import { type ErrorType } from "@vega-api/configs/customClientQuery";
import { getErrorMessage } from "@vega-api/custom/utils/errorHandling";
import {
  FeatureFlag,
  useFeatureFlag,
} from "@vega-api/extended/auth/useFeatureFlag";
import { useInvitationStart } from "@vega-api/generated/auth-invitation-v1/auth-invitation-v1";
import { useWorkbenchWorkbenchInvitationStart } from "@vega-api/generated/auth-invitation-workbench-workbench-v1/auth-invitation-workbench-workbench-v1";
import { type InvitationPermissionEntry } from "@vega-api/generated/types/invitationPermissionEntry";
import { type InvitationStartRequest } from "@vega-api/generated/types/invitationStartRequest";
import { type PermissionEntry } from "@vega-api/generated/types/permissionEntry";
import { type PortalInvitationStartRequest } from "@vega-api/generated/types/portalInvitationStartRequest";
import { SubjectType } from "@vega-api/generated/types/subjectType";
import { type BaseMultiSelectOption } from "@vega-core/components/Form/Controls/Dropdown/ThemedMultiselectDropdown/ThemedMultiselectDropdown";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";

import { type CollaboratorsState } from "../components/InviteCollaboratorsDialog";

const getInitialCollaboratorState = (index: number): CollaboratorsState => ({
  id: Date.now(),
  index,
  applyAllFund: true,
  selectedRole: {
    label: "",
    value: "",
  },
  selectedFunds: [],
});

export const useCollaborators = (setOpen: (open: boolean) => void) => {
  const [collaboratorsAccordions, setCollaboratorsAccordions] = useState<
    CollaboratorsState[]
  >([getInitialCollaboratorState(1)]);

  const showToast = useToast();
  const { featureFlag } = useFeatureFlag();

  const isInvitationV2 = featureFlag[FeatureFlag.GP_AUTH_INVITATION_V2];

  const invitationMutationOptions = {
    mutation: {
      onSuccess: () => {
        showToast({
          title: m.userManagement_invite_success(),
          variant: "success",
        });
        handleClose();
      },
      onError: (error: ErrorType<unknown>) => {
        const { title, description } = getErrorMessage(error, {
          defaultTitle: m.userManagement_invite_error(),
        });
        showToast({
          title,
          description,
          variant: "destructive",
        });
      },
    },
  };

  const legacyInvitation = useInvitationStart(invitationMutationOptions);
  const workbenchWorkbenchInvitation = useWorkbenchWorkbenchInvitationStart(
    invitationMutationOptions,
  );

  const isPending = isInvitationV2
    ? workbenchWorkbenchInvitation.isPending
    : legacyInvitation.isPending;

  const resetCollaborators = () => {
    setCollaboratorsAccordions([getInitialCollaboratorState(1)]);
  };

  const handleClose = useCallback(() => {
    resetCollaborators();
    setOpen(false);
  }, [setOpen]);

  const updateAccordion = (
    id: number,
    updater: (accordion: CollaboratorsState) => CollaboratorsState,
  ) => {
    setCollaboratorsAccordions((prev) =>
      prev.map((accordion) =>
        accordion.id === id ? updater(accordion) : accordion,
      ),
    );
  };

  const handleFundSelectionChange = (
    id: number,
    newSelectedFunds: BaseMultiSelectOption[],
  ) => {
    updateAccordion(id, (accordion) => ({
      ...accordion,
      selectedFunds: newSelectedFunds,
    }));
  };

  const handleApplyAllFundsToggle = (id: number) => {
    updateAccordion(id, (accordion) => ({
      ...accordion,
      applyAllFund: !accordion.applyAllFund,
      selectedFunds: [],
    }));
  };

  const handleRoleChange = (
    id: number,
    newSelectedRole: BaseMultiSelectOption,
  ) => {
    updateAccordion(id, (accordion) => ({
      ...accordion,
      selectedRole: newSelectedRole,
    }));
  };

  const deleteCollaboratorsAccordion = (id: number) => {
    setCollaboratorsAccordions((prev) =>
      prev
        .filter((collaboratorsAccordion) => collaboratorsAccordion.id !== id)
        .map((accordion, newIndex) => ({
          ...accordion,
          index: newIndex + 1,
        })),
    );
  };

  const handleAddAccordion = () => {
    const newAccordion = getInitialCollaboratorState(
      collaboratorsAccordions.length + 1,
    );
    setCollaboratorsAccordions((prev) => [...prev, newAccordion]);
  };

  const isValid = () => {
    if (collaboratorsAccordions.length === 0) return false;

    return collaboratorsAccordions.every((accordion) => {
      if (!accordion.selectedRole.value) return false;

      return accordion.applyAllFund || accordion.selectedFunds.length > 0;
    });
  };

  const handleInviteCollaborators = useCallback(
    (values: { email: string }) => {
      // --- New portal-pair API (workbench-workbench) ---
      if (isInvitationV2) {
        const entries: InvitationPermissionEntry[] = [];

        for (const collaboratorsAccordion of collaboratorsAccordions) {
          if (collaboratorsAccordion.applyAllFund) {
            entries.push({
              roleId: collaboratorsAccordion.selectedRole.id ?? "",
              subjectType: SubjectType.FUND,
            });
          } else {
            for (const fund of collaboratorsAccordion.selectedFunds) {
              entries.push({
                roleId: collaboratorsAccordion.selectedRole.id ?? "",
                subjectType: SubjectType.FUND,
                subjectId: fund.value,
              });
            }
          }
        }

        const payload: PortalInvitationStartRequest = {
          email: values.email,
          permissions: { entries },
        };

        workbenchWorkbenchInvitation.mutate({ data: payload });

        return;
      }

      // --- Legacy (auth-invitation-v1). Delete this branch once
      // gp_auth_invitation_v2 is fully rolled out. ---
      const entries: PermissionEntry[] = [];

      for (const collaboratorsAccordion of collaboratorsAccordions) {
        if (collaboratorsAccordion.applyAllFund) {
          entries.push({
            subjectId: undefined,
            roleId: collaboratorsAccordion.selectedRole.id ?? "",
            subjectType: SubjectType.FUND,
          });
        } else {
          for (const fund of collaboratorsAccordion.selectedFunds) {
            entries.push({
              subjectId: fund.value,
              roleId: collaboratorsAccordion.selectedRole.id ?? "",
              subjectType: SubjectType.FUND,
            });
          }
        }
      }

      const payload: InvitationStartRequest = {
        email: values.email,
        permissions: { entries },
      };

      legacyInvitation.mutate({ data: payload });
    },
    [
      isInvitationV2,
      workbenchWorkbenchInvitation,
      legacyInvitation,
      collaboratorsAccordions,
    ],
  );

  return {
    collaboratorsAccordions,
    handleFundSelectionChange,
    handleApplyAllFundsToggle,
    handleRoleChange,
    deleteCollaboratorsAccordion,
    handleAddAccordion,
    isValid,
    handleInviteCollaborators,
    isPending,
    handleClose,
  };
};
